import { isGenerativeTaskLoose } from '@/shared/domain/taskTypes';
import { buildTaskKey } from './taskKeys';

export function isTrainingCompleted(training) {
  const status = (training?.status || '').toLowerCase();
  return (
    status === 'completed' ||
    status === 'ready to deploy' ||
    status === 'ready_to_deploy'
  );
}

export function isDeployEligibleTraining(training) {
  if (!isTrainingCompleted(training)) return false;
  const taskType = (training?.task_type || '').toLowerCase();
  if (isGenerativeTaskLoose(taskType)) return false;
  if (training.is_deployed) return false;
  return true;
}

/**
 * Trainings eligible for the Add Task picker (excludes already-selected usecases).
 */
export function filterEligibleTrainings(trainings, mode, selectedTasks = []) {
  const selectedUsecaseIds = new Set(
    selectedTasks.map((entry) => entry.training.llm_usecase_id)
  );
  const isEligible =
    mode === 'deploy' ? isDeployEligibleTraining : isTrainingCompleted;

  return (trainings || []).filter((training) => {
    if (!training?.llm_usecase_id || training.version == null) return false;
    if (selectedUsecaseIds.has(training.llm_usecase_id)) return false;
    return isEligible(training);
  });
}

export function formatTrainingPickerLabel(training) {
  const name =
    training.objective_name || training.usecase_name || 'Model';
  const version =
    training.version != null ? ` v${training.version}` : '';
  const project = training.group_name ? ` (${training.group_name})` : '';
  return `${name}${version}${project}`;
}

export function findTrainingByTaskKey(trainings, taskKey) {
  return (trainings || []).find((t) => buildTaskKey(t) === taskKey) || null;
}

export function formatVersionOptionLabel(version) {
  if (version == null) return 'V—';
  const n = Number(version);
  if (Number.isInteger(n)) return `V${n}`;
  return `V${version}`;
}

export function taskDisplayName(training) {
  return training.objective_name || training.usecase_name || 'Task';
}

/** Unique usecase options for a task row (excludes usecases selected in other rows). */
export function buildTaskSelectOptions(
  trainings,
  mode,
  selectedTasks = [],
  currentUsecaseId = null
) {
  const selectedInOtherRows = new Set(
    selectedTasks
      .filter((entry) => entry.training.llm_usecase_id !== currentUsecaseId)
      .map((entry) => entry.training.llm_usecase_id)
  );
  const isEligible =
    mode === 'deploy' ? isDeployEligibleTraining : isTrainingCompleted;

  const byUsecase = new Map();
  (trainings || []).forEach((training) => {
    if (!training?.llm_usecase_id) return;
    const isCurrent = training.llm_usecase_id === currentUsecaseId;
    if (!isEligible(training) && !isCurrent) return;
    if (selectedInOtherRows.has(training.llm_usecase_id)) return;
    if (!byUsecase.has(training.llm_usecase_id)) {
      byUsecase.set(training.llm_usecase_id, training);
    }
  });

  return Array.from(byUsecase.values())
    .sort((a, b) => taskDisplayName(a).localeCompare(taskDisplayName(b)))
    .map((training) => ({
      value: training.llm_usecase_id,
      label: taskDisplayName(training),
    }));
}

/** Completed / deploy-eligible versions for one usecase. */
export function buildVersionSelectOptions(
  trainings,
  mode,
  usecaseId,
  currentVersion = null
) {
  if (!usecaseId) return [];
  const isEligible =
    mode === 'deploy' ? isDeployEligibleTraining : isTrainingCompleted;

  return (trainings || [])
    .filter((training) => {
      if (training.llm_usecase_id !== usecaseId) return false;
      if (isEligible(training)) return true;
      return (
        currentVersion != null &&
        Number(training.version) === Number(currentVersion)
      );
    })
    .sort((a, b) => (b.version || 0) - (a.version || 0))
    .map((training) => ({
      value: training.version,
      label: formatVersionOptionLabel(training.version),
    }));
}

export function findTrainingForUsecaseVersion(trainings, usecaseId, version) {
  return (
    (trainings || []).find(
      (training) =>
        training.llm_usecase_id === usecaseId &&
        Number(training.version) === Number(version)
    ) || null
  );
}

export function findLatestTrainingForUsecase(trainings, mode, usecaseId) {
  const versions = buildVersionSelectOptions(trainings, mode, usecaseId);
  if (!versions.length) return null;
  return findTrainingForUsecaseVersion(trainings, usecaseId, versions[0].value);
}
